/**
 * useContextRegression — step back when the surrounding context disappears.
 *
 * A step often depends on some transient container being open: "click Move Out"
 * only makes sense while the row menu is showing. If that container goes away
 * without the step completing, the user backed out, and sitting on a step whose
 * target no longer exists is a dead end. So the guide returns to the step that
 * re-opens it.
 *
 * Two subtleties this encodes:
 *
 *  1. **Completion beats regression.** Progressing frequently destroys the context
 *     in the same tick — clicking Move Out closes the row menu (the context) at
 *     the same moment it opens the confirm dialog (the completion signal). Without
 *     the guard the two race on their poll intervals and the guide can bounce
 *     backwards out of a step the user just finished.
 *
 *  2. **Visibility, not existence.** Several drawers here are mounted permanently
 *     and merely parked off-screen when closed, so a bare querySelector would
 *     report the context as present forever and this would never fire.
 *
 * Extracted from HelpGuide so it can be verified directly: that component can't be
 * mounted outside a real session (its role gate returns null).
 */

import { useEffect } from 'react';
import { selectorVisible, isSatisfiedNow } from './detectors.js';

const POLL_MS = 400;

/**
 * Consecutive misses required before backing out.
 *
 * One missed tick is not enough evidence. Clicking a menu item destroys the
 * context and reveals the successor in the same instant, but the successor is
 * not necessarily *visible* in the same instant: `.rp-drawer` slides in over
 * 300ms (residents-page.css), and until it overlaps the viewport
 * `selectorVisible` correctly reports false. With a single-miss rule the guide
 * could conclude "they backed out" during the slide and jump backwards — which
 * is exactly what the pendant flow's "Choose Manage Devices" step did, while the
 * move-out flow got away with it only because its dialog mounts instantly
 * instead of animating.
 *
 * Two misses spans 800ms, comfortably past any transition in this app, and still
 * backs out promptly on a real cancel.
 */
const MISSES_BEFORE_REGRESS = 2;

/**
 * @param {object} args
 * @param {object|null} args.step — active step (needs .context and .done)
 * @param {Array} args.steps
 * @param {boolean} args.finished
 * @param {() => string} args.getPathname
 * @param {(index: number) => void} args.onRegress
 * @param {number} [args.pollMs]
 * @param {number} [args.missesBeforeRegress]
 */
export default function useContextRegression({
    step,
    steps,
    finished,
    getPathname,
    onRegress,
    pollMs = POLL_MS,
    missesBeforeRegress = MISSES_BEFORE_REGRESS,
}) {
    useEffect(() => {
        if (!step?.context?.selector || finished) return undefined;

        /* Only regress once the context has actually been seen — a container that
           hasn't mounted yet must not look like one that vanished. */
        let seen = false;
        let misses = 0;

        const id = setInterval(() => {
            if (selectorVisible(step.context.selector)) {
                seen = true;
                misses = 0;
                return;
            }
            if (!seen) return;
            if (isSatisfiedNow(step.done, { pathname: getPathname() })) {
                /* The successor arrived — completion is imminent, so any misses
                   counted while it was animating in were noise. */
                misses = 0;
                return;
            }

            misses += 1;
            if (misses < missesBeforeRegress) return;

            const target = steps.findIndex((s) => s.id === step.context.contextStep);
            if (target >= 0) onRegress(target);
        }, pollMs);

        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, steps, finished, pollMs, missesBeforeRegress]);
}
